import { useState, useEffect } from 'react';
import { CtyButton, formatPageLabels, getSourceClipTimeLabel, ID_CONSTANTS, sharedPDFViewHandler, pdfViewPayload, SourceElementType, handleZipFileViewer, handleDitamapFileViewer, ArticleLayout, AgentViewPayload } from '@circuitry-ai/doc-ui';
import { RootState, useGetAgentKowledgeSourceMutation, useGetGuestPdfLinkForConversationsMutation, useGetPartsZipComponentsMutation, useGetPdfLinkForConversationsMutation, useGetPdfLinkMutation } from '@circuitry-ai/doc-data';
import { ExcelFileViewModal } from './excelFileViewModal';
import { useSelector } from 'react-redux';

interface KnowledgeSourceUIProps {
    metadataMap: Record<string, any>;
    advisorId: string;
    inlineTriggerSource: any;
    inlineTriggerLoading: string;
    setInlineTriggerLoading: (value: string) => void;
    agentSource?: boolean;
    hideSources?: boolean;
}
interface ExcelKnowledgeData {
    isExcelModalOpen: boolean;
    source: any;
}

/** DITA catalog zips (e.g. dita_catalog__….zip) open the table-of-contents viewer. */
const isDitaCatalogZipSource = (source: any) => {
    const ref = source?.element_refs?.[0] ?? {};
    const fileName = String(source?.file_name ?? source?.original_file_name ?? '');
    const extension = String(
        ref.file_extension ?? source?.source_extension ?? source?.file_extension ?? ''
    ).toLowerCase();
    const isZip = extension === SourceElementType.ZIP || /\.zip$/i.test(fileName);
    const isDitamap =
        source?.element_type === SourceElementType.DITAMAP ||
        source?.source_type === SourceElementType.DITAMAP ||
        ref.file_type === SourceElementType.DITAMAP ||
        /^dita_catalog__/i.test(fileName);
    return isZip && isDitamap;
};

const KnowledgeSourceUI = (props: KnowledgeSourceUIProps) => {
    const { metadataMap = {}, advisorId, inlineTriggerSource, inlineTriggerLoading, setInlineTriggerLoading, agentSource = false, hideSources = false } = props;
    const messageId = metadataMap?.message_id ?? '';

    // Unique key per rendered chip/loading indicator (source + first page label + message + index).
    const buildLoadingState = (id: any, source: any, index: number | null) =>
        id + (source?.page_labels?.length > 0 ? source?.page_labels?.[0] : '') + messageId + index;
    const { guestToken } = useSelector((state: RootState) => state.advisor);
    const { token: authToken } = useSelector((state: RootState) => state.auth);
    const [getPdfLink] = useGetPdfLinkForConversationsMutation();
    const [getGuestPdfLink] = useGetGuestPdfLinkForConversationsMutation();
    const [getAgentPdfLink] = useGetAgentKowledgeSourceMutation();
    const [getPdfLinkV2] = useGetPdfLinkMutation();
    const [pdfLoadingIndex, setPdfLoadingIndex] = useState<string>('');
    const [ArticleId, setArticleId] = useState<string | null>(null);
    const [isArticleReadOnly, setIsArticleReadOnly] = useState(false);
    const [fetchZipComponents] = useGetPartsZipComponentsMutation();

    const [excelFileKnowledgeView, setExcelFileKnowledgeView] = useState<ExcelKnowledgeData>({
        isExcelModalOpen: false,
        source: null,
    });

    useEffect(() => {
        const handleInlineTrigger = async () => {
            if (inlineTriggerLoading && inlineTriggerSource) {
                await handleButtonAction(inlineTriggerSource, null, inlineTriggerSource.ref_id);
            }
        };
        handleInlineTrigger();
    }, [inlineTriggerLoading, inlineTriggerSource]);

    const handleExcelFileView = (source: any) => {
        setExcelFileKnowledgeView((prev) => ({ ...prev, isExcelModalOpen: true, source: source }));
        // Reset loading state for inline references
        if (inlineTriggerLoading) {
            setInlineTriggerLoading('');
        }
    };

    const handleButtonAction = async (source: any, index: number | null, inlineRefId: string | null) => {
        if (source?.element_type === SourceElementType.YOUTUBE || source?.element_type === SourceElementType.URL || source?.element_type === SourceElementType.API) {
            window.open(source?.element_refs?.[0].url, '_blank');
            // Reset loading state for inline references
            if (inlineRefId) {
                setInlineTriggerLoading('');
            }
        } else if (source?.element_type === SourceElementType.EXCEL) {
            handleExcelFileView(source);
        } else if (source?.element_type === SourceElementType.PARTS_ZIP) {
            const loadingState = buildLoadingState(source?.source_id, source, index);
            inlineRefId ? setInlineTriggerLoading(inlineRefId) : setPdfLoadingIndex(loadingState);
            handleZipFileViewer({ ...source, advisor_id: advisorId }, fetchZipComponents, setPdfLoadingIndex, 'converse', setInlineTriggerLoading);
        } else if (isDitaCatalogZipSource(source)) {
            // Same viewer as the Knowledge tab filename click: left-side topic menu + content.
            // Citations often include sub_source_id for the cited topic; the catalog source_id
            // is still the zip, and the viewer selects that topic inside the menu.
            const loadingState = buildLoadingState(source?.source_id, source, index);
            inlineRefId ? setInlineTriggerLoading(inlineRefId) : setPdfLoadingIndex(loadingState);
            const getDitaPdfLink = authToken ? getPdfLinkV2 : getGuestPdfLink;
            handleDitamapFileViewer({ ...source, advisor_id: advisorId }, getDitaPdfLink, setPdfLoadingIndex, 'converse', setInlineTriggerLoading);
        } else if (
            source?.element_type === SourceElementType.EXPERT_ANSWER ||
            source?.element_type === SourceElementType.KNOWLEDGE_ARTICLE ||
            source?.element_refs?.[0]?.file_type === SourceElementType.EXPERT_ANSWER ||
            source?.element_refs?.[0]?.file_type === SourceElementType.KNOWLEDGE_ARTICLE
        ) {
            const loadingState = buildLoadingState(source?.source_id, source, index);
            inlineRefId ? setInlineTriggerLoading(inlineRefId) : setPdfLoadingIndex(loadingState);
            setIsArticleReadOnly(true);
            setArticleId(source?.source_id);
        } else {
            handlePDFViewer(source, index, messageId, source?.element_type, setInlineTriggerLoading);
        }
    };



    const handlePDFViewer = (source: any, index: number | null, messageId: string, fileType: any, setInlineTriggerLoading: (value: string) => void) => {
        // Check if this is a ditamap file and use sub_source_id instead
        const isDitaMap = source?.element_refs?.[0]?.file_type === 'ditamap';
        const sourceIdToUse = isDitaMap && source?.element_refs?.[0]?.sub_source_id
            ? source.element_refs[0].sub_source_id
            : source?.source_id;

        let pdfLinkRequestPayload: AgentViewPayload | pdfViewPayload;

        if (agentSource) {
            pdfLinkRequestPayload = {
                source_ids: [
                    {
                        source_id: sourceIdToUse,
                        file_name: source?.file_name,
                        file_extension: source?.element_type
                    }
                ]
            }

        }
        else {
            // Get the original source object to access the full list of page_labels
            // We try to match by source_id and ranking if available, otherwise just source_id
            const originalSource = metadataMap?.source_ids?.find((s: any) =>
                s.source_id === sourceIdToUse &&
                (source.ranking !== undefined ? s.ranking === source.ranking : true)
            );

            // Fallback to finding just by source_id if exact match with ranking fails
            const bestMatchSource = originalSource || metadataMap?.source_ids?.find((s: any) => s.source_id === sourceIdToUse);

            const pageLabelsToUse = bestMatchSource?.page_labels || source?.page_labels || [];

            pdfLinkRequestPayload = {
                advisor_id: advisorId,
                source_ids: [
                    {
                        source_id: sourceIdToUse,
                        page_labels: pageLabelsToUse?.map(Number) || []
                    }
                ]
            };
        }

        const apiPayLoad = {
            ...pdfLinkRequestPayload,
            ...(!authToken && { token: guestToken }),
        }

        const loadingState = buildLoadingState(sourceIdToUse, source, index);
        const sourdId = metadataMap?.source_ids;

        const getPdfDetails = authToken ? (agentSource ? getAgentPdfLink : getPdfLink) : getGuestPdfLink;

        sharedPDFViewHandler(loadingState, setPdfLoadingIndex, apiPayLoad, getPdfDetails, sourdId, fileType, source, setInlineTriggerLoading);
    };

    // Dedupe key: same source_id + first page label are still distinct clips when their
    // element_refs[0] start/end times differ (e.g. multiple video segments from one source).
    const sourceDedupeKey = (s: any) => {
        const ref = s?.element_refs?.[0] ?? {};
        return `${s.source_id}-${s.page_labels?.[0]}-${ref.start_time ?? ''}-${ref.end_time ?? ''}`;
    };

    const getFilteredSources = () => {
        const sourceIds = metadataMap?.source_ids ?? [];
        return sourceIds
            .slice()
            .sort((a: any, b: any) => (a.ranking ?? Infinity) - (b.ranking ?? Infinity))
            .flatMap((obj: any) =>
                obj?.page_labels?.length > 0
                    ? Array.from(new Set<number>(obj.page_labels)).map((label: number) => ({ ...obj, page_labels: [label] }))
                    : [obj]
            )
            .filter((source: any, index: number, self: any[]) =>
                index === self.findIndex((s: any) => sourceDedupeKey(s) === sourceDedupeKey(source))
            )
            .slice(0, 5);
    };

    const filteredSources = getFilteredSources();

    return (
        <div className="flex gap-2 flex-col w-full items-start ">
            {!hideSources && <div className='flex flex-col justify-start'>

                <div className="flex flex-wrap gap-1">
                    {filteredSources.map((source: any, index: number) => {

                        const isLoading = (pdfLoadingIndex === buildLoadingState(source.source_id, source, index))
                        const fileName = (source?.file_name && source.file_name.length > 10) ? source?.file_name?.slice(0, 10) + '...' : (source?.file_name || index + 1);
                        const baseTooltip = ((source?.element_type === SourceElementType.TEXT || source?.element_type === SourceElementType.TABLE) && source?.page_labels?.length > 0) ? `${source?.file_name}: ${source?.page_labels?.length > 1 ? "pages" : "page"}(${formatPageLabels(source?.page_labels)})` : `${source?.file_name}`;
                        const tooltipContent = `${baseTooltip}${getSourceClipTimeLabel(source)}`;
                        const ID = source?.file_name === '' ? ID_CONSTANTS.expertSource + index : ID_CONSTANTS.viewSource + index;

                        return (
                            <CtyButton
                                id={ID}
                                key={source.source_id + index}
                                className="text-[#0BA5EC] hover:bg-content1 px-0 font-normal"
                                size="sm"
                                variant="light"
                                onPress={() => handleButtonAction(source, index, null)}
                                isLoading={!inlineTriggerLoading && isLoading}
                                tooltipContent={tooltipContent}
                                toolTipColor='warning'
                            >
                                {`[${fileName}]`}
                            </CtyButton>
                        );
                    })}
                </div>
            </div>}

            {ArticleId && <ArticleLayout articleId={ArticleId} messageId={metadataMap?.message_id} sessionId={metadataMap?.session_id} advisorId={advisorId} readOnly={isArticleReadOnly} conversationView onClose={() => {
                setArticleId(null);
                setIsArticleReadOnly(false);
                // Stop loading after the modal is closed
                setPdfLoadingIndex('');
                setInlineTriggerLoading('');
            }} />}
            {excelFileKnowledgeView?.isExcelModalOpen && <ExcelFileViewModal excelFileKnowledgeView={excelFileKnowledgeView} onClose={() => setExcelFileKnowledgeView((prev) => ({ ...prev, isExcelModalOpen: false }))} advisorId={advisorId} />}

        </div>
    );
};

export default KnowledgeSourceUI;